import { useContext } from 'react';

import styled from '@emotion/styled';

import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import { Money, Select } from '../../../components/Layout/PagePieces';
import Page from '../../../components/Page/Page';
import Table from '../../../components/Table/Table';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import { TableContext } from '../../../components/Table/TableContext';
import { formatNaira } from '../../../utils/utils';
import SessionDetailSlider from './SessionDetailSlider';

// Conversations and whether each made or lost money (limits spec P6).
const Sessions = () => (
  <Page title='Hethera | Sessions'>
    <PageLayout>
      <PageHeader title='Sessions' className='no-bottom-padding' />
      <Table
        tableHeader={{
          Started: 'started_at',
          Customer: 'user_name',
          Messages: 'messages_in',
          'Claude calls': 'llm_calls',
          Purchases: 'purchases',
          Revenue: 'revenue',
          Costs: 'cost',
          Profit: 'profit',
        }}
        tableDataSourceUrl='api/v1/admin/sessions'
        size={20}
        border
        tableFilterBoxFeatures={{ rightAttr: [{ type: 'custom-filter', comp: <SessionFilters /> }] }}
        cellsCustomComp={{
          started_at: <TableDateTimeFormatter dataKey='started_at' />,
          user_name: <CustomerCell />,
          messages_in: <MessagesCell />,
          revenue: <MoneyCell dataKey='revenue' />,
          cost: <MoneyCell dataKey='cost' />,
          profit: <MoneyCell dataKey='profit' coloured />,
        }}
        clickRowShowSliderComp={(row) => <SessionDetailSlider data={row} />}
        hasClickableRow
        emptyStateMsg='No conversations in this range yet'
      />
    </PageLayout>
  </Page>
);

export default Sessions;

const SessionFilters = () => {
  const table = useContext(TableContext);
  const set = (key: string, value: string) => table?.handleSetTableFilterRecord({ [key]: value, page: 0 });
  return (
    <Filters>
      <Select defaultValue='' aria-label='Outcome' onChange={(e) => set('outcome', e.target.value)}>
        <option value=''>All sessions</option>
        <option value='profitable'>Made money</option>
        <option value='loss'>Lost money</option>
        <option value='no_purchase'>No purchase</option>
      </Select>
      <Select defaultValue='-started' aria-label='Sort' onChange={(e) => set('sort', e.target.value)}>
        <option value='-started'>Newest first</option>
        <option value='profit'>Biggest loss first</option>
        <option value='-profit'>Biggest profit first</option>
      </Select>
    </Filters>
  );
};

interface CellProps {
  data?: Record<string, any>;
  dataKey?: string;
  coloured?: boolean;
}

const MoneyCell: React.FC<CellProps> = ({ data, dataKey, coloured }) => {
  const value = Number(data && dataKey ? data[dataKey] : 0);
  return <Money className={coloured ? (value < 0 ? 'loss' : 'gain') : ''}>{formatNaira(value)}</Money>;
};

const CustomerCell: React.FC<CellProps> = ({ data }) => <>{data?.user_name ?? data?.chat_id ?? '—'}</>;

const MessagesCell: React.FC<CellProps> = ({ data }) => (
  <>
    {data?.messages_in ?? 0} in / {data?.messages_out ?? 0} out
  </>
);

const Filters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
